import { useAuth } from "@/context/AuthContext";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Check, X, Trash2, GitBranch, ArrowUpRight } from "lucide-react";
import { num } from "@/lib/format";

export function ProposedReview({ onChange }) {
  const { user: _me } = useAuth();
  const _connectorMode = !!_me?.features?.assistant_claude;
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [reasons, setReasons] = useState({});

  const load = () => api.get("/leads/proposed").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const act = async (lead, kind) => {
    const reason = reasons[lead.lead_id] || "";
    if ((kind === "reject" || kind === "junk") && !reason.trim()) { toast.error("Add a short reason first"); return; }
    setBusy(lead.lead_id + kind);
    try {
      const { data } = await api.post(`/leads/${lead.lead_id}/${kind}`, kind === "approve" ? undefined : { reason });
      if (kind === "approve" && data?.revision && data?.href) {
        toast.success(data.message || "Opening the bid to start a revision");
        setItems((p) => p.filter((x) => x.lead_id !== lead.lead_id));
        onChange?.();
        setOpen(false);
        navigate(data.href);
        return;
      }
      toast.success(kind === "approve" ? "Added to the pipeline" : kind === "junk" ? "Deleted — it won't come back" : "Rejected");
      setItems((p) => p.filter((x) => x.lead_id !== lead.lead_id));
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };

  if (items.length === 0) return null;

  if (_connectorMode) return null;
  return (
    <>
      <button data-testid="proposed-review-banner" onClick={() => setOpen(true)}
        className="w-full mb-4 flex items-center gap-3 rounded-lg border border-[#F4C400]/60 bg-[#F4C400]/10 px-4 py-3 text-left hover:bg-[#F4C400]/20 transition">
        <span className="grid place-items-center w-7 h-7 rounded-full bg-[#003E87] text-white shrink-0"><Sparkles className="w-4 h-4" /></span>
        <span className="flex-1 text-sm text-[#5c4a00]">
          <span className="font-semibold">{items.length} new opportunit{items.length === 1 ? "y" : "ies"} detected from your email</span> — review to approve, reject, or delete.
        </span>
        <span className="text-xs font-semibold text-[#003E87]">Review →</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="proposed-review-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detected opportunities — needs your approval</DialogTitle>
            <DialogDescription>Opportunities flagged from email. Approve to add to the pipeline, or reject/delete with a reason.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {items.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">All caught up.</div>}
            {items.map((l) => (
              <Card key={l.lead_id} data-testid={`proposed-item-${l.lead_id}`} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-black truncate">{l.project_name || "Untitled"}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {l.company || "Unknown client"}{l.contact_email ? ` · ${l.contact_email}` : ""}
                      {l.capacity_mw ? ` · ${num(l.capacity_mw)} MW` : ""}{l.state ? ` · ${l.state}` : ""}
                    </div>
                    {l.notes && <div className="text-xs text-slate-400 mt-1 line-clamp-2">{l.notes}</div>}
                    {(l.rfq_subject || l.detect_confidence != null) && (
                      <div className="mt-2 rounded-md bg-slate-50 border border-slate-100 px-2.5 py-1.5" data-testid={`proposed-why-${l.lead_id}`}>
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                          <Sparkles className="w-3 h-3 text-[#003E87]" /> Why this was flagged
                          {l.detect_confidence != null && <span className="ml-auto font-mono text-[#003E87]">{Math.round((l.detect_confidence || 0) * 100)}% match</span>}
                        </div>
                        {l.rfq_subject && <div className="text-xs text-slate-600 mt-1 truncate" title={l.rfq_subject}>“{l.rfq_subject}”</div>}
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          Detected a request to bid{l.capacity_mw ? ` · ${num(l.capacity_mw)} MW` : ""}{l.state ? ` · ${l.state}` : ""}{l.contact_email ? ` · from ${l.contact_email}` : ""}
                        </div>
                      </div>
                    )}
                    {l.revision_of && (
                      <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-[#003E87] bg-[#003E87]/5 rounded px-1.5 py-0.5">
                        <GitBranch className="w-3 h-3" /> Looks like a revision of “{l.revision_of.bid_name}”
                        <button className="ml-1 inline-flex items-center gap-0.5 hover:underline" onClick={() => navigate(`/bids/${l.revision_of.bid_id}`)}>open bid <ArrowUpRight className="w-3 h-3" /></button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button size="sm" data-testid={`approve-${l.lead_id}`} disabled={busy} onClick={() => act(l, "approve")}
                    className="bg-[#003E87] hover:bg-[#00336f] text-white h-8"><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                  <Input data-testid={`reason-${l.lead_id}`} placeholder="Reason (for reject / delete)" className="h-8 flex-1 min-w-[160px] text-sm"
                    value={reasons[l.lead_id] || ""} onChange={(e) => setReasons((p) => ({ ...p, [l.lead_id]: e.target.value }))} />
                  <Button size="sm" variant="outline" data-testid={`reject-${l.lead_id}`} disabled={busy} onClick={() => act(l, "reject")} className="h-8"><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                  <Button size="sm" variant="outline" data-testid={`junk-${l.lead_id}`} disabled={busy} onClick={() => act(l, "junk")} className="h-8 text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete junk</Button>
                </div>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
